import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { Trans } from 'react-i18next';

import AnswerErrLink from './answer_error_link';
import Err from 'models/err';
import { default as Languages } from 'models/languages/answer_languages';

export const ChatQuickActionErrorAction = Object.freeze({
  DELETE: 'delete',
  SAVE: 'save',
});

export function isQuickActionError(err) {
  return _.get(err, 'meta.usage') === 'QUICK_ACTION';
}

export default function ChatQuickActionErrorMessage({ action, errors }) {
  const groups = _.values(_.groupBy(errors, err => groupKey(action, err)));
  return (
    <span>
      {groups.map((group, index) => (
        <React.Fragment key={groupKey(action, group[0])}>
          {index > 0 && ' '}
          {renderGroupMessage(action, group)}
        </React.Fragment>
      ))}
    </span>
  );
}

ChatQuickActionErrorMessage.propTypes = {
  action: PropTypes.oneOf(_.values(ChatQuickActionErrorAction)).isRequired,
  errors: PropTypes.arrayOf(PropTypes.instanceOf(Err)).isRequired,
};

function groupKey(action, err) {
  if (action === ChatQuickActionErrorAction.DELETE) {
    return err.code;
  }
  const { answerLanguage, length, maxLength } = err.meta;
  const detail = err.code === Err.Code.TOO_LONG || err.code === Err.Code.IN_USE ? undefined : err.detail;
  return [err.code, answerLanguage, length, maxLength, detail].join('|');
}

function renderGroupMessage(action, errors) {
  const widgets = renderWidgetLinks(errors);
  const { answerLanguage, length, maxLength } = errors[0].meta;
  const language = Languages[answerLanguage] || answerLanguage;

  if (action === ChatQuickActionErrorAction.DELETE) {
    return (
      <Trans
        components={{ widgets }}
        i18nKey="This answer can't be deleted while a quick action in <widgets/> uses it."
      />
    );
  }
  if (errors[0].code === Err.Code.TOO_LONG) {
    return (
      <Trans
        components={{ widgets }}
        i18nKey="A quick action in <widgets/> can't send {{language}} public content longer than {{maxLength}} characters (this content's HTML is {{length}})."
        values={{ language, length, maxLength }}
      />
    );
  }
  if (errors[0].code === Err.Code.IN_USE) {
    return (
      <Trans
        components={{ widgets }}
        i18nKey="This change removes the {{language}} public content that a quick action in <widgets/> sends."
        values={{ language }}
      />
    );
  }
  return _.upperFirst(errors[0].detail);
}

function renderWidgetLinks(errors) {
  const endpoints = _.uniqBy(errors, 'meta.endpointId');
  return (
    <>
      {endpoints.map((err, index) => (
        <React.Fragment key={err.meta.endpointId}>
          {index > 0 && ', '}
          <AnswerErrLink href={`/admin/chat/${encodeURIComponent(err.meta.endpointId)}`}>
            {err.meta.address}
          </AnswerErrLink>
        </React.Fragment>
      ))}
    </>
  );
}
